/* eslint-disable react-hooks/exhaustive-deps */
import { EVENT } from "@/constants/event";
import { LOCAL_STORAGE_KEY } from "@/constants/localStorageKey";
import useAppStore from "@/context/store";
import histotyDbService from "@/utils/historyDB";
import { useThirdLoginCallback } from "@/utils/hooks/useThirdLogin";
import { useMemoizedFn } from "ahooks";
import classNames from "classnames";
import { useRouter } from "next/router";
import { useCallback, useMemo, useState } from "react";
import { Case, Default, Switch } from "react-if";
import { logout } from "../../services";
import loginTracker from "../../tracker/report/loginTracker";
import registTracker from "../../tracker/report/registTracker";
import HBIcon from "../HBIcon";
import HBModal from "../HBModal";
import ModalWrap, { destroyAll } from "../HBModal/ModalWrap";
import Image from "../Image";
import styles from "./index.module.less";
import Login, { LoginType } from "./login";
import Register from "./register";
import ResetPwd from "./resetPwd";
import { recordRegister } from "./shared";
import Tab from "./shared/tab";
import { ThirdLogin } from "./shared/third";
import UserAgreement from "./shared/userAgreement";
import { IframeWxQrcode, WxLogin } from "./shared/wxLogin";
// 判断用户是否保存了配置信息，如果有的话替换本地的配置信息
export const checkPageSetting = (user) => {
  if (user?.user_config?.configs) {
    const pageSettingStr = JSON.stringify(user.user_config.configs);
    if (
      pageSettingStr !==
      window.localStorage.getItem(LOCAL_STORAGE_KEY.HUABAN_PAGE_SETTING)
    ) {
      window.localStorage.setItem(
        LOCAL_STORAGE_KEY.HUABAN_PAGE_SETTING,
        pageSettingStr
      );
      window.dispatchEvent(new Event(EVENT.PAGE_SETTING_CHANGE));
    }
  }
};

function LoginView({
  initType = LoginType.WECHAT,
  onOk,
  onCancel,
  initialTel = "",
  title,
}) {
  const [type, setType] = useState(initType);
  const [defaultTel, setDefaultTel] = useState(initialTel);
  const { setUser, config } = useAppStore((s) => ({
    setUser: s.setUser,
    config: s.config,
  }));

  const onSwitchLogin = useCallback((tel) => {
    setDefaultTel(typeof tel === "string" ? tel : "");
    setType(LoginType.PWD);
  }, []);

  const onLogin = useCallback((data) => {
    loginTracker({
      userId: data.user_id,
      login_type: "mobile",
      is_cache_login: false,
    });
    setUser(data);
    onOk && onOk(data);
    histotyDbService.changeUser(data);
  }, []);

  const onRegister = useCallback((data, type) => {
    registTracker(type || "mobile", data.user_id);
    recordRegister();
    setUser(data);
    onOk && onOk(data);
  }, []);

  const { onWxLoginOk, onThirdLoginOk } = useThirdLoginCallback({
    onOk,
  });

  return (
    <>
      {![LoginType.RESET, LoginType.WECHAT].includes(type) && (
        <Tab active={type} onChangeTab={setType} />
      )}
      <Switch>
        <Case condition={type === LoginType.WECHAT}>
          <div
            className={styles.wechatLogin}
            data-dialog-title="微信扫码弹窗"
            data-gd-expose="dialog_expose"
          >
            <div className={styles.title}>{title || "微信扫码登录"}</div>
            <div className={styles.sub}>未注册的微信号将自动创建花瓣账号</div>
            <div className={styles.wechatQrcodeWrap}>
              {config.wxIframe ? (
                <IframeWxQrcode onLogin={onWxLoginOk} />
              ) : (
                <WxLogin onLogin={onWxLoginOk} />
              )}

              <div>
                <HBIcon type="ic_wechat1" />
                微信扫码
              </div>
            </div>
            <div className={styles.otherLogin}>
              <div className={styles.phone} onClick={onSwitchLogin}>
                <HBIcon type="icon" />
                账号登录注册
              </div>
              <ThirdLogin onOk={onThirdLoginOk} />
              <div
                className={classNames([styles.thirdLoginItem, styles.email])}
                data-gd-click="button_click"
                data-button-name="邮箱登录"
                onClick={() => {
                  setType(LoginType.EMAIL);
                }}
                style={{
                  marginLeft: 12,
                }}
              >
                <HBIcon className={styles.icon} type="email" title="邮箱" />
              </div>
            </div>
            <UserAgreement className={styles.bottom} />
          </div>
        </Case>
        <Case condition={type === LoginType.REGISTER}>
          <Register
            setType={setType}
            onSwitchLogin={onSwitchLogin}
            onOk={onRegister}
            onThirdLogin={onThirdLoginOk}
          />
        </Case>
        <Case condition={type === LoginType.RESET}>
          <ResetPwd
            initialTel={defaultTel}
            onCancel={onSwitchLogin}
            onOk={onSwitchLogin}
          />
        </Case>
        <Default>
          <Login
            type={type}
            setType={setType}
            initialTel={defaultTel}
            onOk={onLogin}
            onCancel={onCancel}
            setDefaultTel={setDefaultTel}
            onThirdLogin={onThirdLoginOk}
          />
        </Default>
      </Switch>
    </>
  );
}

/**
 * 登录弹窗
 * @param {{initType: LoginType, title: string} & import("antd").ModalProps} param0
 */
function LoginModal({ initType = LoginType.WECHAT, title, ...props }) {
  const { user, userReady } = useAppStore((s) => ({
    user: s.user,
    userReady: s.userReady,
  }));

  if (user || !userReady) {
    return null;
  }

  return (
    <HBModal
      className={styles.modal}
      width={480}
      footer={null}
      closable={true}
      closeIcon={<HBIcon type="ic_clear2" />}
      {...props}
    >
      <LoginView initType={initType} title={title} {...props} />
    </HBModal>
  );
}

const LoginDialog = ModalWrap(LoginModal, { unique: true });

export default LoginDialog;

export function useLogin() {
  const router = useRouter();

  return useMemo(() => {
    return {
      async login(props = {}) {
        return new Promise((resolve) => {
          // 关闭其他弹框，避免同时出现多个弹框
          destroyAll();
          LoginDialog.open({
            ...props,
            async onOk(data) {
              resolve(data);
            },
            onCancel() {
              resolve(false);
            },
          });
        });
      },

      async logout(redirect, isBlank) {
        await logout();
        useAppStore.getState().setUser(null);
        if (redirect) {
          isBlank
            ? (window.location.href = redirect)
            : router.push(redirect || "/");
        }
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export function LoginCard({
  initType = LoginType.WECHAT,
  onOk,
  loginState,
  ...props
} = {}) {
  const [isLogin, setIsLogin] = useState(loginState || false);

  const okWrap = useMemoizedFn(async (data) => {
    setIsLogin(true);
    onOk?.(data);
  });

  return (
    <div className={styles.loginCard}>
      {isLogin ? (
        <div className={styles.loginSucess}>
          <Image
            src="https://st0.dancf.com/static/02/202404180434-aba5.svg"
            width={84}
            alt="操作成功"
          />
          <div>操作成功</div>
        </div>
      ) : (
        <LoginView initType={initType} onOk={okWrap} {...props} />
      )}
    </div>
  );
}
